import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import invariant from 'tiny-invariant'

import { gql } from '~/__generated__'
import { Button } from '~/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/ui/card'
import { Badge } from '~/ui/badge'
import { GraphqlError } from '~/ui/errors'
import { toast } from '~/hooks/use-toast'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { billingPath } from '~/common/paths'
import { useCurrentOrganization } from '~/auth/use-current-organization'

const ZONE_BILLING_CONTEXT_QUERY = gql(`
  query ZoneBillingContext($zoneId: ID!, $organizationId: ID!) {
    zone(id: $zoneId) {
      id
      name
      location {
        id
        name
      }
    }
    billingPlans(zoneId: $zoneId) {
      edges {
        node {
          id
          name
          slug
          monthlyBasePriceCents
          entitlements
          restrictions
          active
        }
      }
    }
    organizationSubscriptions(organizationId: $organizationId) {
      edges {
        node {
          id
          status
          currentPeriodStart
          currentPeriodEnd
          cancelAtPeriodEnd
          billingPlan {
            id
            name
            slug
            monthlyBasePriceCents
          }
          zone {
            id
            name
          }
          billingSubscriptionItems {
            id
            quantity
            zone {
              id
              name
            }
            billingPlan {
              id
              name
              slug
              monthlyBasePriceCents
            }
          }
        }
      }
    }
  }
`)

const CREATE_CHECKOUT_SESSION_MUTATION = gql(`
  mutation CreateCheckoutSessionForZone($input: CreateCheckoutSessionInput!) {
    createCheckoutSession(input: $input) {
      checkoutSession {
        url
        sessionId
      }
      errors
    }
  }
`)

const SYNC_STRIPE_CHECKOUT_SESSION_MUTATION = gql(`
  mutation SyncStripeCheckoutSession($input: SyncStripeCheckoutSessionInput!) {
    syncStripeCheckoutSession(input: $input) {
      success
      errors
      subscription {
        id
        status
        currentPeriodEnd
        billingPlan {
          id
          name
          monthlyBasePriceCents
        }
        zone {
          id
        }
      }
    }
  }
`)

export const ZoneBillingScreen = () => {
  const { zoneId } = useParams()
  invariant(zoneId)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { id: orgId } = useCurrentOrganization()

  const [createCheckoutSession] = useSafeMutation(CREATE_CHECKOUT_SESSION_MUTATION)
  const [syncStripeCheckoutSession] = useSafeMutation(SYNC_STRIPE_CHECKOUT_SESSION_MUTATION)

  const { data, error, loading, refetch } = useQuery(ZONE_BILLING_CONTEXT_QUERY, {
    variables: { zoneId, organizationId: orgId },
    skip: !orgId,
    fetchPolicy: 'cache-and-network',
  })

  const zone = data?.zone
  const plans = data?.billingPlans?.edges?.map((e: any) => e.node) || []
  const subscriptions = data?.organizationSubscriptions?.edges?.map((e: any) => e.node) || []

  const zoneSubscription = useMemo(() => {
    // Match via per-zone line items (current model) OR the legacy zone FK.
    const zoneSubs = subscriptions.filter(
      (s: any) =>
        s.zone?.id === zoneId ||
        s.billingSubscriptionItems?.some((i: any) => i.zone?.id === zoneId),
    )
    // Prefer active/trialing subscription over cancelled/expired ones
    return (
      zoneSubs.find((s: any) => s.status === 'active' || s.status === 'trialing') ||
      zoneSubs.find((s: any) => s.status === 'past_due') ||
      zoneSubs[0] ||
      null
    )
  }, [subscriptions, zoneId])

  const [isSyncing, setIsSyncing] = useState(false)

  useEffect(() => {
    const success = searchParams.get('success')
    const checkoutSessionId = searchParams.get('checkout_session_id')
    if (success && checkoutSessionId && !isSyncing) {
      setIsSyncing(true)
      ;(async () => {
        try {
          const res = await syncStripeCheckoutSession({
            variables: { input: { checkoutSessionId, zoneId } },
          })

          if (res.data?.syncStripeCheckoutSession?.success) {
            toast({ title: 'Subscription synced' })
            await refetch()
            // Clean the URL
            navigate(`/zones/${zoneId}/billing`, { replace: true })
          } else {
            toast({
              title: 'Sync failed',
              description:
                res.data?.syncStripeCheckoutSession?.errors?.join(', ') ||
                'Unable to sync subscription',
              variant: 'destructive',
            })
          }
        } finally {
          setIsSyncing(false)
        }
      })()
    }
  }, [navigate, refetch, searchParams, syncStripeCheckoutSession, zoneId, isSyncing])

  if (error) return <GraphqlError error={error} />

  if (!orgId) {
    return (
      <div className="p-6">
        <h1 className="text-2xl font-semibold">Zone billing</h1>
        <p className="text-muted-foreground mt-2">
          Missing organization context. Select an organization, then return to{' '}
          <Button variant="link" onClick={() => navigate(billingPath({}))}>
            Billing
          </Button>
          .
        </p>
      </div>
    )
  }

  if (loading || !zone) {
    return <div className="p-6">Loading…</div>
  }

  const formattedPrice = (cents?: number | null) => {
    if (!cents && cents !== 0) return '—'
    return `$${(cents / 100).toFixed(0)}/mo`
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
      case 'trialing':
        return (
          <Badge variant="default" className="bg-green-500">
            Active
          </Badge>
        )
      case 'past_due':
        return <Badge variant="destructive">Past Due</Badge>
      case 'canceled':
        return <Badge variant="secondary">Canceled</Badge>
      case 'incomplete':
      case 'incomplete_expired':
        return <Badge variant="outline">Incomplete</Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const handleUpgrade = async (planId: string) => {
    const successUrl = `${window.location.origin}/zones/${zoneId}/billing?success=1&checkout_session_id={CHECKOUT_SESSION_ID}`
    const cancelUrl = `${window.location.origin}/zones/${zoneId}/billing?canceled=1`

    const result = await createCheckoutSession({
      variables: {
        input: {
          planId,
          zoneId,
          successUrl,
          cancelUrl,
        },
      },
    })

    const session = result.data?.createCheckoutSession?.checkoutSession
    const errs = result.data?.createCheckoutSession?.errors || []
    if (session?.url) {
      window.location.href = session.url
      return
    }

    // Card on file: the backend added the zone as a line item inline and
    // returns a session with no redirect URL — that's success, not an error.
    if (session && errs.length === 0) {
      toast({ title: 'Zone added to your subscription' })
      await refetch()
      return
    }

    toast({
      title: 'Error',
      description: errs.length ? errs.join(', ') : 'Failed to create checkout session',
      variant: 'destructive',
    })
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold">Billing</h1>
          <p className="mt-1 text-gray-600">
            {zone.location?.name} — {zone.name}
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(billingPath({}))}>
          Back to billing overview
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Current subscription</CardTitle>
        </CardHeader>
        <CardContent>
          {zoneSubscription ? (
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="text-sm font-medium">{zoneSubscription.billingPlan?.name}</div>
                <div className="text-xs text-gray-500">
                  Status: {getStatusBadge(zoneSubscription.status)}
                  {zoneSubscription.currentPeriodEnd ? (
                    <span className="ml-2">
                      Renews: {new Date(zoneSubscription.currentPeriodEnd).toLocaleDateString()}
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="text-sm font-semibold">
                {formattedPrice(zoneSubscription.billingPlan?.monthlyBasePriceCents)}
              </div>
            </div>
          ) : (
            <div className="text-muted-foreground">No active subscription for this zone.</div>
          )}
        </CardContent>
      </Card>

      <div>
        <h2 className="text-xl font-semibold">Available plans</h2>
        <p className="text-muted-foreground mt-1 text-sm">
          Plans shown here are specific to this zone's location (plus global plans).
        </p>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {plans.map((plan: any) => (
            <Card key={plan.id}>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{plan.name}</span>
                  <span className="text-sm font-semibold">
                    {formattedPrice(plan.monthlyBasePriceCents)}
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-xs text-gray-500">Slug: {plan.slug}</div>
                <Button
                  className="w-full"
                  onClick={() => handleUpgrade(plan.id)}
                  disabled={zoneSubscription?.billingPlan?.id === plan.id}
                >
                  {zoneSubscription?.billingPlan?.id === plan.id
                    ? 'Current plan'
                    : 'Subscribe / change plan'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  )
}
